/* ============================================================================
   MUG BED THEME — front-end behaviour
   ----------------------------------------------------------------------------
   Unlike the static prototype this was built from, the cart here is the real
   Shopify cart. Everything goes through /cart/add.js, /cart/change.js and
   /cart.js, so quantities and totals always match what checkout will charge.
   ========================================================================== */
(function () {
  'use strict';

  const T = window.THEME || {};

  /* ------------------------------------------------------------ utilities */

  /* Shopify money formatting, driven by the shop's own money_format string. */
  function formatMoney(cents) {
    const fmt = T.moneyFormat || '{{amount}}';
    const value = Number(cents || 0);

    function withDelimiter(num, precision, thousands, decimal) {
      if (isNaN(num)) return '0';
      const fixed = (num / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const int = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':                  return withDelimiter(value, 2, ',', '.');
        case 'amount_no_decimals':      return withDelimiter(value, 0, ',', '.');
        case 'amount_with_comma_separator':     return withDelimiter(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiter(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiter(value, 2, "'", '.');
        default:                        return withDelimiter(value, 2, ',', '.');
      }
    });
  }

  function toast(message) {
    const stack = document.querySelector('[data-toasts]');
    if (!stack) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M20 6.5 9.5 17 4 11.5"/></svg><span></span>';
    el.querySelector('span').textContent = message;
    stack.appendChild(el);
    setTimeout(() => el.remove(), 3200);
  }

  /* --------------------------------------------------------------- bundles */

  /* Tiers are read from the bundle picker's data attributes, which the Liquid
     section writes out from theme settings. */
  function readTiers(root) {
    const el = root && root.querySelector('[data-bundle-tiers]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function tierFor(tiers, qty) {
    let best = null;
    tiers.forEach(t => { if (qty >= t.qty && (!best || t.qty >= best.qty)) best = t; });
    return best || { qty: 1, discount: 0 };
  }

  /* ------------------------------------------------------------------ cart */

  const Cart = {
    state: null,

    async fetch() {
      const res = await fetch(T.cartUrl + '.js', { headers: { Accept: 'application/json' } });
      this.state = await res.json();
      this.render();
      return this.state;
    },

    async add(variantId, quantity) {
      const res = await fetch(T.cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: quantity }] })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      return this.fetch();
    },

    async changeLine(line, quantity) {
      const res = await fetch(T.cartChangeUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      this.state = await res.json();
      this.render();
      return this.state;
    },

    render() {
      const cart = this.state;
      if (!cart) return;
      const count = cart.item_count;

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.classList.toggle('is-visible', count > 0);
      });
      document.querySelectorAll('[data-cart-open]').forEach(el => {
        el.setAttribute('aria-label',
          count ? 'Open cart, ' + count + ' item' + (count === 1 ? '' : 's') : 'Open cart, empty');
      });

      const body = document.querySelector('[data-cart-body]');
      const foot = document.querySelector('[data-cart-foot]');
      if (!body) return;

      if (!count) {
        body.innerHTML =
          '<div class="cart-empty">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M4 6h13v7a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5Z"/><path d="M17 8.5h1.8a2.7 2.7 0 0 1 0 5.4H17"/><path d="M8.5 12h.01M12.5 12h.01"/></svg>' +
          '<p>Your cart is empty &mdash; no cat has been served.</p>' +
          '<a class="btn" href="' + (T.shopUrl || '/') + '">Shop the bed</a></div>';
        if (foot) foot.hidden = true;
        return;
      }

      /* /cart.js already has Shopify's automatic bundle discounts applied
         (final_line_price / total_price), so show those as-is — taking a
         second discount off here would show less than checkout charges. */
      let compareSubtotal = 0;

      body.innerHTML = cart.items.map((item, i) => {
        const line = i + 1;
        const finalLine = item.final_line_price != null ? item.final_line_price : item.line_price;
        const img = item.image
          ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x160$1$2')
          : '';
        const unit = item.quantity ? Math.round(finalLine / item.quantity) : item.price;
        const compareAt = T.compareAtPrice;
        const hasCompare = compareAt && compareAt > item.price;
        const wasPrice = hasCompare
          ? '<s class="cart-line__was">' + formatMoney(compareAt) + '</s> ' : '';
        compareSubtotal += hasCompare ? compareAt * item.quantity : item.original_line_price;

        return '' +
          '<div class="cart-line">' +
            (img ? '<img class="cart-line__img" src="' + img + '" alt="" width="80" height="80" loading="lazy">' : '') +
            '<div class="cart-line__info">' +
              // Long SEO product titles pack extra keyword phrases after a
              // "|" — the cart only needs the first, human-facing part.
              '<p class="cart-line__title">' + item.product_title.split('|')[0].trim() + '</p>' +
              '<p class="cart-line__variant">' +
                (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title + ' &middot; ' : '') +
                wasPrice + formatMoney(unit) + ' each</p>' +
              '<div class="cart-line__row">' +
                '<div class="qty">' +
                  '<button type="button" data-line-dec="' + line + '" aria-label="Decrease quantity">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg></button>' +
                  '<input type="number" value="' + item.quantity + '" min="1" data-line-qty="' + line + '" aria-label="Quantity">' +
                  '<button type="button" data-line-inc="' + line + '" aria-label="Increase quantity">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>' +
                '</div>' +
                '<span class="cart-line__price">' + formatMoney(finalLine) + '</span>' +
              '</div>' +
              '<button class="link-btn" type="button" data-line-remove="' + line + '">Remove</button>' +
            '</div>' +
          '</div>';
      }).join('');

      /* Nudge toward the next bundle tier, when tiers are live. */
      if (T.bundlesActive) {
        const tiers = window.__BUNDLE_TIERS || [];
        const next = tiers.find(t => t.qty > count);
        if (next) {
          const need = next.qty - count;
          body.insertAdjacentHTML('beforeend',
            '<button class="cart-nudge" type="button" data-line-set="' + next.qty + '">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 12h15M13 5.5 19.5 12 13 18.5"/></svg>' +
            '<span>Add ' + need + ' more and save <strong>' +
            Math.round(next.discount * 100) + '%</strong> on the whole order</span></button>');
        }
      }

      const finalTotal = cart.total_price;
      const totalSaving = Math.max(0, compareSubtotal - finalTotal);
      const totalEl = document.querySelector('[data-cart-total]');
      if (totalEl) totalEl.textContent = formatMoney(finalTotal);

      const totalWasEl = document.querySelector('[data-cart-total-was]');
      if (totalWasEl) {
        if (compareSubtotal > finalTotal) {
          totalWasEl.textContent = formatMoney(compareSubtotal);
          totalWasEl.hidden = false;
        } else {
          totalWasEl.hidden = true;
        }
      }

      const savedRow = document.querySelector('[data-cart-saved]');
      if (savedRow) {
        savedRow.hidden = totalSaving <= 0;
        const amt = savedRow.querySelector('[data-cart-saved-amount]');
        if (amt) amt.textContent = '−' + formatMoney(totalSaving);
      }
      if (foot) foot.hidden = false;
    }
  };

  /* -------------------------------------------------------- drawer opening */

  let lastFocus = null;

  function openCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (!drawer) return;
    lastFocus = document.activeElement;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const close = drawer.querySelector('[data-cart-close]');
    if (close) close.focus();
  }

  function closeCart() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    setTimeout(() => { overlay.hidden = true; }, 300);
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function trapFocus(e) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || !drawer.classList.contains('is-open') || e.key !== 'Tab') return;
    const f = drawer.querySelectorAll('button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* --------------------------------------------------------------- header */

  function initHeader() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.getElementById('mobile-nav');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      toggle.setAttribute('aria-label', open ? 'Open menu' : 'Close menu');
      menu.hidden = open;
      toggle.querySelectorAll('svg').forEach((s, i) => { s.hidden = open ? i === 1 : i === 0; });
    });
  }

  /* --------------------------------------------------------- product page */

  function initProduct() {
    const root = document.querySelector('[data-pdp]');
    if (!root) return;

    /* Gallery */
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = root.querySelector('[data-gallery-thumbs]');
    if (thumbs && main) {
      thumbs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-thumb]');
        if (!btn) return;
        const img = main.querySelector('img');
        // The server-rendered <img> carries a responsive `srcset` (from
        // Shopify's image_tag sizes/widths params). Browsers that support
        // srcset use it in preference to `src`, so setting `src` alone here
        // silently does nothing visible — srcset must be cleared too.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        // Fade keyed off the image's own load/error event, not rAF — rAF can
        // sit paused on some mobile browsers, which would leave the photo
        // stuck invisible. The timeout is just a backstop for the one case
        // neither fires: re-clicking the already-showing thumbnail.
        const reveal = () => { img.style.opacity = '1'; };
        img.onload = reveal;
        img.onerror = reveal;
        img.style.opacity = '0';
        img.src = btn.dataset.full;
        img.alt = btn.dataset.alt || '';
        setTimeout(reveal, 400);
        thumbs.querySelectorAll('[data-thumb]').forEach(t =>
          t.setAttribute('aria-current', String(t === btn)));
      });

      /* Prev/next arrows on the main image just step through the same
         thumbnail buttons, so the click handler above stays the single
         source of truth for what "select this image" does. Scrolling the
         active thumbnail into view is done by hand (thumbs.scrollTo) rather
         than el.scrollIntoView() — that call is free to walk up and scroll
         ancestor containers too, and on some mobile browsers that dragged
         the whole page sideways along with the thumbnail strip. */
      const prevBtn = root.querySelector('[data-gallery-prev]');
      const nextBtn = root.querySelector('[data-gallery-next]');
      function centerThumb(el) {
        const target = el.offsetLeft - (thumbs.clientWidth - el.clientWidth) / 2;
        thumbs.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      function stepGallery(offset) {
        const all = Array.from(thumbs.querySelectorAll('[data-thumb]'));
        const current = all.findIndex(t => t.getAttribute('aria-current') === 'true');
        const target = all[(current + offset + all.length) % all.length];
        if (!target) return;
        target.click();
        centerThumb(target);
      }
      if (prevBtn) prevBtn.addEventListener('click', () => stepGallery(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => stepGallery(1));

      /* Touch swipe on the main photo, mainly for mobile where there's no
         cursor to hover the arrow buttons. Only acts once the gesture is
         clearly more horizontal than vertical, so an ordinary page scroll
         that starts on the image still scrolls the page instead of
         fighting it for the gesture. */
      let touchStartX = 0;
      let touchStartY = 0;
      let touchActive = false;
      main.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchActive = true;
      }, { passive: true });
      main.addEventListener('touchmove', (e) => {
        if (!touchActive || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) e.preventDefault();
      }, { passive: false });
      main.addEventListener('touchend', (e) => {
        if (!touchActive) return;
        touchActive = false;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          stepGallery(dx < 0 ? 1 : -1);
        }
      });
    }

    /* Bundle picker doubles as the quantity selector. */
    const tiers = readTiers(root);
    window.__BUNDLE_TIERS = tiers;
    const wrap = root.querySelector('[data-bundles]');
    const qtyField = root.querySelector('[name="quantity"]');
    let qty = 1;

    /* Every price shown on this page — here, in the bundle cards (rendered
       server-side in Liquid) and in the cart — measures "you save" against
       the same reference: compare-at price × quantity. Two different
       baselines (real price for bundle math, compare-at for the headline
       badge) is how the mismatch bug happened last time, so this is the
       one formula for the whole page. */
    function paintPrice() {
      const unitCents = Number(root.dataset.unitPrice || 0);
      const compareCents = Number(root.dataset.comparePrice || 0);
      const tier = tierFor(tiers, qty);

      const realGross = unitCents * qty;
      // Shopify takes the percentage off each bed and drops the fraction of a
      // cent, so mirror that exactly or 3 beds can differ from checkout by 2¢.
      const unitOff = T.bundlesActive ? Math.floor(unitCents * tier.discount + 1e-7) : 0;
      const bundleOff = unitOff * qty;
      const net = realGross - bundleOff;

      const compareGross = compareCents > unitCents ? compareCents * qty : 0;
      const saved = compareGross > net ? compareGross - net : 0;
      const savedPct = compareGross > 0 ? Math.round((saved / compareGross) * 100) : 0;

      const now = root.querySelector('[data-pdp-now]');
      const was = root.querySelector('[data-pdp-was]');
      const badge = root.querySelector('[data-pdp-badge]');
      const unit = root.querySelector('[data-pdp-unit]');

      if (now) now.textContent = formatMoney(net);

      if (saved > 0) {
        if (was) { was.textContent = formatMoney(compareGross); was.hidden = false; }
        if (badge) {
          badge.textContent = 'Save ' + formatMoney(saved) + ' · ' + savedPct + '% off';
          badge.hidden = false;
        }
      } else {
        if (was) was.hidden = true;
        if (badge) badge.hidden = true;
      }

      if (unit) {
        unit.textContent = qty === 1
          ? (root.dataset.unitNote || '')
          : formatMoney(Math.round(net / qty)) + ' per bed · ' + qty + ' beds';
      }

      const barPrice = document.querySelector('[data-buybar-price]');
      if (barPrice) barPrice.textContent = formatMoney(net);
      const barNote = document.querySelector('[data-buybar-note]');
      if (barNote) {
        barNote.textContent = saved > 0
          ? 'Save ' + savedPct + '% vs. ' + formatMoney(compareGross)
          : 'Free shipping — unlocked';
      }
      if (qtyField) qtyField.value = qty;
    }

    if (wrap) {
      wrap.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-bundle]');
        if (!btn) return;
        qty = Number(btn.dataset.bundle);
        wrap.querySelectorAll('[data-bundle]').forEach(b =>
          b.setAttribute('aria-checked', String(b === btn)));
        paintPrice();
      });

      wrap.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].indexOf(e.key) === -1) return;
        const opts = Array.prototype.slice.call(wrap.querySelectorAll('[data-bundle]'));
        const i = opts.indexOf(document.activeElement);
        if (i === -1) return;
        e.preventDefault();
        const step = (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? 1 : -1;
        const next = opts[(i + step + opts.length) % opts.length];
        next.focus();
        next.click();
      });
    }
    paintPrice();

    /* Add to cart — the form is intercepted so the drawer can open in place. */
    const form = document.getElementById('ProductForm');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add]');
        const id = form.querySelector('[name="id"]').value;
        if (btn) { btn.disabled = true; btn.dataset.label = btn.textContent; }
        try {
          await Cart.add(Number(id), qty);
          toast(qty + ' × ' + (root.dataset.productTitle || 'item') + ' added');
          openCart();
        } catch (err) {
          toast(err.message);
        } finally {
          if (btn) btn.disabled = false;
        }
      });
    }

    /* Sticky mobile buy bar */
    const bar = document.querySelector('[data-buybar]');
    const anchor = root.querySelector('[data-buy-anchor]');
    if (bar && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible',
          !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 }).observe(anchor);
    }
    const barAdd = document.querySelector('[data-buybar] [data-add]');
    if (barAdd && form) barAdd.addEventListener('click', () => form.requestSubmit());
  }

  /* ------------------------------------------------------- urgency countdown */

  function initUrgencyBar() {
    const bar = document.querySelector('[data-urgency-bar]');
    if (!bar) return;
    const d = bar.querySelector('[data-u-d]');
    const h = bar.querySelector('[data-u-h]');
    const m = bar.querySelector('[data-u-m]');
    const s = bar.querySelector('[data-u-s]');
    if (!h || !m || !s) return;

    /* Counts down to the merchant's fixed end date when one is set and still
       in the future; otherwise resets to the next local midnight, so the
       bar always shows a live countdown rather than freezing at zero. */
    function nextTarget() {
      const raw = bar.dataset.end;
      if (raw) {
        const fixed = new Date(raw).getTime();
        if (!isNaN(fixed) && fixed > Date.now()) return fixed;
      }
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      return midnight.getTime();
    }

    let target = nextTarget();
    const pad = (n) => String(n).padStart(2, '0');

    function tick() {
      let diff = target - Date.now();
      if (diff <= 0) {
        target = nextTarget();
        diff = target - Date.now();
      }
      if (d) d.textContent = pad(Math.floor(diff / 86400000));
      h.textContent = pad(Math.floor((diff % 86400000) / 3600000));
      m.textContent = pad(Math.floor((diff % 3600000) / 60000));
      s.textContent = pad(Math.floor((diff % 60000) / 1000));
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ------------------------------------------------------- home sticky bar */

  function initStickyBar() {
    const bar = document.querySelector('[data-sticky-bar]');
    if (!bar) return;

    const hero = document.querySelector('.hero');
    const footer = document.querySelector('.footer');

    if (hero && 'IntersectionObserver' in window) {
      let pastHero = false;
      let atFooter = false;
      const paint = () => bar.classList.toggle('is-visible', pastHero && !atFooter);

      new IntersectionObserver(([entry]) => {
        pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        paint();
      }, { threshold: 0 }).observe(hero);

      if (footer) {
        new IntersectionObserver(([entry]) => {
          atFooter = entry.isIntersecting;
          paint();
        }, { threshold: 0, rootMargin: '0px 0px -10% 0px' }).observe(footer);
      }
    } else {
      bar.classList.add('is-visible');
    }

    const addBtn = bar.querySelector('[data-sticky-add]');
    if (!addBtn) return;
    addBtn.addEventListener('click', async () => {
      const id = Number(addBtn.dataset.variantId);
      addBtn.disabled = true;
      try {
        await Cart.add(id, 1);
        toast('Added to cart');
        openCart();
      } catch (err) {
        toast(err.message);
      } finally {
        addBtn.disabled = false;
      }
    });
  }

  /* ---------------------------------------------------------- scroll reveal */

  function initReveal() {
    const targets = document.querySelectorAll('.reveal');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(t => t.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    targets.forEach((t, i) => {
      t.style.transitionDelay = Math.min(i % 4, 3) * 70 + 'ms';
      io.observe(t);
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    initHeader();
    initProduct();
    initStickyBar();
    initUrgencyBar();
    initReveal();
    Cart.fetch();

    document.addEventListener('click', async (e) => {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); return openCart(); }
      if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) return closeCart();

      const inc = e.target.closest('[data-line-inc]');
      if (inc) {
        const line = Number(inc.dataset.lineInc);
        return Cart.changeLine(line, Cart.state.items[line - 1].quantity + 1);
      }
      const dec = e.target.closest('[data-line-dec]');
      if (dec) {
        const line = Number(dec.dataset.lineDec);
        return Cart.changeLine(line, Cart.state.items[line - 1].quantity - 1);
      }
      const rm = e.target.closest('[data-line-remove]');
      if (rm) {
        await Cart.changeLine(Number(rm.dataset.lineRemove), 0);
        return toast('Removed from cart');
      }
      const jump = e.target.closest('[data-line-set]');
      if (jump) return Cart.changeLine(1, Number(jump.dataset.lineSet));
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (input) Cart.changeLine(Number(input.dataset.lineQty), parseInt(input.value, 10) || 1);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCart();
      trapFocus(e);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
